import PropTypes from "prop-types"
import React, { useEffect, useState, useMemo } from "react"
import { Link } from "react-router-dom"
import { withTranslation } from "react-i18next"
import { Modal, ModalBody } from "reactstrap"
import { toast } from "react-toastify"
import moment from "moment"

// Components
import Breadcrumbs from "../../components/Common/Breadcrumb2"
import TableContainer from "../../components/Common/TableContainer2"
import Spinners from "components/Common/Spinner"
import MyTooltip from "components/MyTooltip"

// Helpers
import { get, del } from "helpers/api_helper"
import "../../components/Common/datatables.scss"

const DeleteCampaignModal = ({ show, onDeleteClick, onCloseClick }) => {
  return (
    <Modal size="md" isOpen={show} toggle={onCloseClick} centered={true}>
      <ModalBody className="px-4 py-4">
        <h5 className="mb-4" style={{ fontWeight: "600" }}>Are you sure to delete?</h5>
        
        <div className="d-flex gap-2 justify-content-end mt-4">
          <button
            type="button"
            className="btn btn-outline-secondary"
            style={{ 
              minWidth: "100px", 
              backgroundColor: "#fff", 
              border: "1px solid #ced4da",
              color: "#495057",
              borderRadius: "8px"
            }}
            onClick={onCloseClick}
          >
            No
          </button>
          <button
            type="button"
            className="btn btn-danger"
            style={{ 
              minWidth: "100px",
              borderRadius: "8px"
            }}
            onClick={onDeleteClick}
          >
            Yes
          </button>
        </div>
      </ModalBody>
    </Modal>
  )
}

const SurveyLineCampaignList = props => {
  const [deleteModal, setDeleteModal] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [contentList, setContentList] = useState([])
  const [contentListLoading, setContentListLoading] = useState(false)

  // Badge style helper
  const badgeStyle = (bgColor, textColor, borderColor) => ({
    backgroundColor: bgColor,
    color: textColor,
    border: `1px solid ${borderColor}`,
    padding: "4px 16px",
    borderRadius: "20px",
    fontWeight: "600",
    fontSize: "13px",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px"
  })

  // Campaign Status Badge
  const getCampaignStatusBadge = (status) => {
    if (!status) return <span className="badge" style={badgeStyle("#f5f5f5", "#616161", "#e0e0e0")}>-</span>
    
    const normalizedStatus = status.toLowerCase()
    
    switch (normalizedStatus) {
      case "draft":
        return <span className="badge" style={badgeStyle("#f5f5f5", "#595959", "#d9d9d9")}>Draft</span>
      case "schedule":
        return <span className="badge" style={badgeStyle("#eef6ff", "#2563eb", "#d1e5ff")}>Schedule</span>
      case "publish":
        return <span className="badge" style={badgeStyle("#e8f5e9", "#2e7d32", "#c8e6c9")}>Publish</span>
      case "failed":
        return <span className="badge" style={badgeStyle("#ffebee", "#c62828", "#ef9a9a")}>Failed</span>
      default:
        return <span className="badge" style={badgeStyle("#f5f5f5", "#616161", "#bdbdbd")}>{status}</span>
    }
  }


  const handleDeleteCampaign = async () => {
    if (!selectedId) return
    try {
      await del(
        process.env.REACT_APP_BACKEND_URL + "/v1/admin/line-campaign/" + selectedId
      )
      setDeleteModal(false)
      toast.success("Campaign deleted successfully")
      setContentList(prevList => prevList.filter(item => item.id !== selectedId))
    } catch (error) {
      console.error("Delete error:", error)
      setDeleteModal(false)
      toast.error("Error deleting campaign")
    }
  }

  const columns = useMemo(() => [
    {
      header: "#",
      enableColumnFilter: false,
      enableSorting: true,
      cell: cellProps => {
        return (
          <div style={{ display: "flex", justifyContent: "center" }}>
            {parseInt(cellProps.row.id) + 1}
          </div>
        )
      },
    },
    {
      header: "Campaign Name",
      accessorKey: "campaign_name",
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Start Date",
      accessorKey: "start_date",
      enableColumnFilter: false,
      enableSorting: true,
      cell: cellProps => {
        return cellProps.row.original.start_date
          ? moment(cellProps.row.original.start_date).format("YYYY-MM-DD")
          : "-"
      },
    },
    {
      header: "End Date",
      accessorKey: "end_date",
      enableColumnFilter: false,
      enableSorting: true,
      cell: cellProps => {
        return cellProps.row.original.end_date
          ? moment(cellProps.row.original.end_date).format("YYYY-MM-DD")
          : "-"
      },
    },
    {
      header: "Campaign Status",
      accessorKey: "status",
      enableColumnFilter: false,
      enableSorting: true,
      cell: cellProps => {
        return getCampaignStatusBadge(cellProps.row.original.status)
      },
    },
    {
      header: "Created Date",
      accessorKey: "created_at",
      enableColumnFilter: false,
      enableSorting: true,
      cell: cellProps => {
        return cellProps.row.original.created_at
          ? moment(cellProps.row.original.created_at).format("YYYY-MM-DD")
          : "-"
      },
    },
    {
      header: "Tools",
      accessorKey: "tools",
      enableColumnFilter: false,
      enableSorting: false,
      cell: cellProps => {
        let _id = cellProps.row.original.id
        const status = (cellProps.row.original.status || "").toLowerCase()
        const canEdit = ["draft", "failed"].includes(status)
        const btnStyle = {
          width: "32px",
          height: "32px",
          padding: "0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }
        return (
          <div className="d-flex gap-1">
            <MyTooltip label="View">
              <Link
                className="btn btn-sm btn-info"
                style={btnStyle}
                to={`/survey-line-campaign/view/${_id}`}
              >
                <i className="bx bx-show"></i>
              </Link>
            </MyTooltip>

            <MyTooltip label="History">
              <Link
                className="btn btn-sm btn-secondary"
                style={btnStyle}
                to={`/survey-line-campaign/history/${_id}`}
              >
                <i className="bx bx-history"></i>
              </Link>
            </MyTooltip>

            {canEdit && (
              <MyTooltip label="Edit">
                <Link
                  className="btn btn-sm btn-warning"
                  style={btnStyle}
                  to={`/survey-line-campaign/edit/${_id}`}
                >
                  <i className="bx bx-edit-alt"></i>
                </Link>
              </MyTooltip>
            )}

            <button
              className="btn btn-sm btn-danger"
              style={btnStyle}
              onClick={() => {
                setSelectedId(_id)
                setDeleteModal(true)
              }}
            >
              <i className="bx bx-trash"></i>
            </button>
          </div>
        )
      },
    },
  ], [])

  useEffect(() => {
    setContentListLoading(true)
    const fetchContentList = async () => {
      try {
        const result = await get(
          process.env.REACT_APP_BACKEND_URL + "/v1/admin/line-campaign"
        )
        setContentList(result.data || [])
      } catch (error) {
        console.log("error", error)
        setContentList([])
      }
      setContentListLoading(false)
    }

    fetchContentList()
  }, [])

  //meta title
  document.title = "Survey Line Campaign | " + process.env.REACT_APP_CMS_TITLE

  return (
    <>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs
            title="Survey Line Campaign"
            breadcrumbItems={[
              { title: "Survey Line Campaign", link: "survey-line-campaign" },
              { title: "List", link: "list" },
            ]}
          />

          <div className="bInnerContent">
            {contentListLoading ? (
              <Spinners setLoading={setContentListLoading} />
            ) : (
              <TableContainer
                isAddButton={true}
                addRowLink={`/create`}
                columns={columns}
                data={contentList || []}
                isGlobalFilter={true}
                isPagination={true}
                SearchPlaceholder="Search"
                pagination="pagination"
                paginationWrapper="dataTables_paginate paging_simple_numbers"
                tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                rootMenu="surveyLineCampaign"
                isExportToExcel={false}
              />
            )}
          </div>
        </div>
      </div>

      <DeleteCampaignModal
        show={deleteModal}
        onDeleteClick={handleDeleteCampaign}
        onCloseClick={() => setDeleteModal(false)}
      />
    </>
  )
}

SurveyLineCampaignList.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(SurveyLineCampaignList)
